import { useCallback, useRef, useState } from 'react';
import { useNavBlocker } from '@/hooks/use-nav-blocker';

export interface UnsavedEditGuard {
  /** The "Discard unsaved changes?" question is open. */
  readonly confirmOpen: boolean;
  /**
   * Runs `action` at once when nothing is unsaved; otherwise asks first and
   * runs it only after the merchant chose to discard. X, Cancel, Escape,
   * edit-mode off, a route change of the frame and "Change template" all go
   * through it.
   */
  readonly guard: (action: () => void) => void;
  /** The guarded close of the dialog itself. */
  readonly requestClose: () => void;
  readonly keepEditing: () => void;
  readonly discard: () => void;
}

/**
 * A21/A22: nothing a merchant typed is dropped silently. While `isDirty`:
 * - the shell blocks admin route changes (`useNavBlocker`, which also
 *   registers the browser's tab-close prompt);
 * - `guard` turns every way out of the dialog into a question.
 * The canvas is inert while the dialog is open, so it cannot navigate either.
 *
 * Epic #228 P4 (PD13): `isDirty` is the dialog's LOCAL-ONLY unsaved work (a
 * touched field the schema refuses, a file on its way). Valid edits live in
 * the auto-save queue, whose own leave guard decides for them.
 */
export function useUnsavedEditGuard({
  isDirty,
  onClose,
}: {
  readonly isDirty: boolean;
  readonly onClose: () => void;
}): UnsavedEditGuard {
  useNavBlocker(isDirty);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingRef = useRef<(() => void) | null>(null);

  const guard = useCallback(
    (action: () => void): void => {
      if (!isDirty) {
        action();
        return;
      }
      pendingRef.current = action;
      setConfirmOpen(true);
    },
    [isDirty],
  );

  const requestClose = useCallback((): void => guard(onClose), [guard, onClose]);

  const keepEditing = useCallback((): void => {
    pendingRef.current = null;
    setConfirmOpen(false);
  }, []);

  const discard = useCallback((): void => {
    const action = pendingRef.current;
    pendingRef.current = null;
    setConfirmOpen(false);
    action?.();
  }, []);

  return { confirmOpen, guard, requestClose, keepEditing, discard };
}
